'use client';
import {Box} from '@mui/material';
import Container from '@mui/material/Container';
import type {Theme} from '@mui/material/styles';
import {alpha, styled} from '@mui/material/styles';
import NextLink from 'next/link';
import {type ElementType, type ReactNode} from 'react';
import {trackHomepageCardClick, trackHomepageLearnMoreClick} from '../../analytics';
import {useAfterFirstPaint} from '../../hooks';
import {ListingCarousel, ListingEmptyState, type ListingEmptyStateProps} from '../listing';
import {Button, Section, SectionHeading, Typography} from '../primitives';

const MAX_ROW_ITEMS = 3;
const BANNER_MIN_HEIGHT_MOBILE = 360;
const BANNER_MIN_HEIGHT_DESKTOP = 500;
const CTA_HOVER_DURATION_MS = 500;
const restScrim = (theme: Theme) =>
  `linear-gradient(180deg, transparent 0%, ${alpha(theme.palette.colors.base.black, 0.27)} 50%, ${alpha(theme.palette.colors.base.black, 0.9)} 100%)`;
const hoverScrim = (theme: Theme) => theme.palette.colors.surface.darkOverlayStrongHover;

const REVEAL_ACTIVE = {
  '& .RevealCardScrim': {opacity: 0},
  '& .RevealCardScrimHover': {opacity: 1},
  '& .RevealCardCtaReveal': {gridTemplateRows: '1fr'},
  '& .RevealCardCta': {opacity: 1},
};

export interface RevealCardProps {
  id?: string;
  title: string;
  image?: string | null;
  href?: string;
  ctaLabel?: string;
}

export interface RevealCardSectionProps {
  title: string;
  subtitle?: string;
  items: RevealCardProps[];
  ctaLabel?: string;
  empty?: ListingEmptyStateProps;
  sectionId?: string;
  sectionName: string;
}

const Banner = styled(Box, {shouldForwardProp: propName => propName !== '$src'})<{
  $src?: string;
  component?: ElementType;
  href?: string;
}>(({theme, $src}) => ({
  position: 'relative',
  isolation: 'isolate',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
  minHeight: BANNER_MIN_HEIGHT_MOBILE,
  borderRadius: theme.radii['2xl'],
  overflow: 'hidden',
  padding: theme.spacing(3.5),
  textAlign: 'left',
  textDecoration: 'none',
  color: theme.palette.colors.text.neutral.inverse,
  backgroundColor: theme.palette.colors.surface.neutral.inverse,
  ...($src && {backgroundImage: `url(${$src})`, backgroundSize: 'cover', backgroundPosition: 'center'}),
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.colors.border.focus.onColor}`,
    outlineOffset: 2,
  },
  [theme.breakpoints.down('md')]: {
    '& .RevealCardCtaReveal': {display: 'none'},
  },
  [theme.breakpoints.up('md')]: {
    minHeight: BANNER_MIN_HEIGHT_DESKTOP,
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(5.75),
    paddingInline: theme.spacing(5),
    '& .RevealCardCta': {
      opacity: 0,
      transition: `opacity ${CTA_HOVER_DURATION_MS}ms ease-out`,
    },
    '@media (hover: hover)': {
      '&:hover, &:focus-visible': REVEAL_ACTIVE,
    },
    '@media (hover: none)': {
      '& .RevealCardCtaReveal': {gridTemplateRows: '1fr'},
      '& .RevealCardCta': {opacity: 1},
    },
  },
}));

const Scrim = styled('span', {shouldForwardProp: propName => propName !== '$hover'})<{$hover?: boolean}>(({theme, $hover}) => ({
  position: 'absolute',
  inset: 0,
  borderRadius: 'inherit',
  pointerEvents: 'none',
  transition: `opacity ${CTA_HOVER_DURATION_MS}ms ease-out`,
  ...($hover ? {backgroundColor: hoverScrim(theme), opacity: 0} : {backgroundImage: restScrim(theme), opacity: 1}),
}));

const Content = styled(Box)({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

const CtaReveal = styled('span')({
  display: 'grid',
  gridTemplateRows: '0fr',
  minHeight: 0,
  transition: `grid-template-rows ${CTA_HOVER_DURATION_MS}ms ease-out`,
});

const CtaClip = styled('span')(({theme}) => ({
  overflow: 'hidden',
  minHeight: 0,
  paddingTop: theme.spacing(1.25), // 10px gap from the title; clipped while the row is collapsed
}));

function RevealCard({title, image, href, ctaLabel, onClick}: RevealCardProps & {onClick?: () => void}) {
  const content = (
    <>
      <Scrim aria-hidden className="RevealCardScrim" />
      <Scrim aria-hidden className="RevealCardScrimHover" $hover />
      <Content>
        <Typography
          size="title-2"
          weight="semiBold"
          mobileSize="title-5"
          mobileWeight="semiBold"
          sx={theme => ({
            textTransform: 'uppercase',
            color: theme.palette.colors.text.neutral.inverse,
            [theme.breakpoints.down('md')]: {textTransform: 'uppercase'},
          })}
        >
          {title}
        </Typography>
        {href && ctaLabel && (
          <CtaReveal aria-hidden className="RevealCardCtaReveal">
            <CtaClip>
              <Button
                variant="outlined-light"
                component="span"
                tabIndex={-1}
                disableRipple
                className="RevealCardCta"
                sx={{pointerEvents: 'none'}}
              >
                {ctaLabel}
              </Button>
            </CtaClip>
          </CtaReveal>
        )}
      </Content>
    </>
  );

  // A CSS background image is never lazy-loaded, so it would otherwise download as soon as the
  // card renders, ahead of the page's first paint.
  const src = useAfterFirstPaint() ? (image ?? undefined) : undefined;

  return href ? (
    <Banner $src={src} component={NextLink} href={href} onClick={onClick}>
      {content}
    </Banner>
  ) : (
    <Banner $src={src}>{content}</Banner>
  );
}

// A lone card keeps its pre-carousel treatment: full-width on mobile, half-width on desktop.
const SingleBanner = styled(Box)(({theme}) => ({
  [theme.breakpoints.up('md')]: {
    width: `calc((100% - ${theme.spacing(4)}) / 2)`,
  },
}));

// Desktop layout for 2–3 cards: a fitted row (2-up or 3-up) that fills the width.
const BannerRow = styled(Box, {shouldForwardProp: propName => propName !== '$cols'})<{$cols: 2 | 3}>(({theme, $cols}) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(4),
  '& > *': {
    flex: '0 0 auto',
    width: `calc((100% - ${theme.spacing(4 * ($cols - 1))}) / ${$cols})`,
  },
}));

export function RevealCardSection({title, subtitle, items, ctaLabel, empty, sectionId, sectionName}: RevealCardSectionProps) {
  const section = {name: sectionName, id: sectionId};
  const cards = items.map((card, index) => (
    <RevealCard
      key={`${card.title}-${index}`}
      {...card}
      ctaLabel={card.ctaLabel ?? ctaLabel}
      onClick={() => {
        const item = {id: card.id ?? '', name: card.title};
        trackHomepageCardClick(item, section);
        trackHomepageLearnMoreClick(section, item);
      }}
    />
  ));

  let body: ReactNode;
  if (empty && items.length === 0) {
    body = (
      <Container>
        <ListingEmptyState {...empty} card tone="inverse" />
      </Container>
    );
  } else if (items.length === 1) {
    // A single card stays a static banner (no carousel) — as before.
    body = (
      <Container>
        <SingleBanner>{cards}</SingleBanner>
      </Container>
    );
  } else if (items.length <= MAX_ROW_ITEMS) {
    // 2–3 cards: desktop keeps the fitted row; mobile scrolls as a carousel
    // (with peek) — matching the Featured Projects / Latest News sections.
    body = (
      <>
        <Box sx={{display: {xs: 'none', md: 'block'}}}>
          <Container>
            <BannerRow $cols={items.length >= 3 ? 3 : 2}>{cards}</BannerRow>
          </Container>
        </Box>
        <Box sx={{display: {xs: 'block', md: 'none'}}}>
          <ListingCarousel arrowTone="dark">{cards}</ListingCarousel>
        </Box>
      </>
    );
  } else {
    // More than three render as a carousel at every breakpoint.
    body = <ListingCarousel arrowTone="dark">{cards}</ListingCarousel>;
  }

  return (
    <Section tone="inverse">
      <Container>
        <SectionHeading
          title={title}
          subtitle={subtitle}
          titleMobileSize="title-5"
          subtitleMobileSize="body-2"
          subtitleFamily="primary"
          tone="inverse"
          sx={{mb: 4}}
        />
      </Container>
      {body}
    </Section>
  );
}

RevealCardSection.displayName = 'RevealCardSection';
